/*
 * What the two gunbarrel viewers write to the session, built as plain
 * functions.
 *
 * Both viewers need WebGL and will not mount in jsdom, so anything left inside
 * their effects is untestable — and the rule these functions carry is the one
 * worth testing: a source the user has not chosen must not be recorded as
 * though they had. Building the payload here means a test exercises the same
 * code the effect runs, rather than a hand-rebuilt copy of it that stays green
 * when the effect itself is changed.
 */
import _omit from "lodash/omit";

import { ThreeDViewerOptionT, ThreeDViewerPersistedStateT } from "models";

/**
 * May this viewer record its model/ipdb source, or is it still showing a
 * fallback nobody has chosen?
 *
 * A source written before the user's settings read has answered is read back by
 * the next mount as saved state, which makes the effect that applies the user's
 * own default return early — and the viewer stays on the fallback for the rest
 * of the session. Restored state is a real choice already and waits for
 * nothing.
 *
 * Deliberately not "does this user have a saved geo record": a user who has
 * never opened the settings page has none and never will, so that question
 * never turns true and the sources would be withheld for ever.
 */
export const shouldPersistViewerSources = (
  hasRestoredSources: boolean,
  haveUserSettingsAnswered: boolean
): boolean => hasRestoredSources || haveUserSettingsAnswered;

/**
 * The 3D viewer's session payload.
 *
 * Everything serializable goes in from the first commit — the dashboard widget
 * shares this key, and its scale, colour map and the rest have to survive a
 * dashboard switch whatever the settings read is doing. Only the two sources
 * wait, which is why the return is a `Partial`: a payload written before the
 * read answers is genuinely a subset, and every reader takes these field by
 * field with a default behind each one.
 *
 * Left out on purpose: `onOptionChange` (a function), the three list fields and
 * `availableWells` (rebuilt from the API and stores at runtime), and the four
 * dimension fields (derived from Redux).
 */
export function buildThreeDPersistedState(
  options: ThreeDViewerOptionT,
  hasRestoredSources: boolean,
  haveUserSettingsAnswered: boolean
): Partial<ThreeDViewerPersistedStateT> {
  return {
    scaleZ: options.scaleZ,
    showDrainage: options.showDrainage,
    showIPDBDrainage: options.showIPDBDrainage,
    showVerticalPortion: options.showVerticalPortion,
    lineWidth: options.lineWidth,
    showIPDBPlane: options.showIPDBPlane,
    showIPDBMesh: options.showIPDBMesh,
    highlightHzLeg: options.highlightHzLeg,
    showMultiLegWells: options.showMultiLegWells,
    hzLegColor: options.hzLegColor,
    wireframe: options.wireframe,
    showWellLabel: options.showWellLabel,
    showWellDetail: options.showWellDetail,
    colorMap: options.colorMap,
    colorMapMin: options.colorMapMin,
    colorMapMax: options.colorMapMax,
    ipdbField: options.ipdbField,
    ipdbFieldUnit: options.ipdbFieldUnit,
    maxValue: options.maxValue,
    scaleDrainage: options.scaleDrainage,
    showVertical: options.showVertical,
    ...(shouldPersistViewerSources(hasRestoredSources, haveUserSettingsAnswered)
      ? { modelSource: options.modelSource, ipdbSource: options.ipdbSource }
      : {})
  };
}

/**
 * The cross-section's session settings, under the same rule.
 *
 * Its two sources are named differently but do the same job, and the settings
 * provider treats a `topsModelSource` that is merely present as a saved choice
 * — so writing one before the read answers makes its apply-the-user-default
 * effect return early for the rest of the session. That is exactly why the well
 * card's seed omits both fields, and a session payload written a moment later
 * would otherwise put them straight back.
 *
 * Generic over the settings object so this module stays clear of the settings
 * store, which the viewer owns.
 */
export function buildXdaPersistedSettings<T extends object>(
  settings: T,
  hasRestoredSources: boolean,
  haveUserSettingsAnswered: boolean
): T | Omit<T, "topsModelSource" | "ipdbSource"> {
  return shouldPersistViewerSources(hasRestoredSources, haveUserSettingsAnswered)
    ? settings
    : _omit(settings, ["topsModelSource", "ipdbSource"]);
}
